import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { GlobalErrorProvider } from './context/GlobalErrorContext';
import RouteMetadata from './components/Common/RouteMetadata';
import './styles/global-error.css';
import './styles/responsive.css';

const Home = lazy(() => import('./pages/Home/Home'));
const AgentPage = lazy(() => import('./pages/Agent/AgentPage'));
const Products = lazy(() => import('./pages/Products/Products'));
const Automation = lazy(() => import('./pages/Products/Automation'));
const Apps = lazy(() => import('./pages/Products/Apps'));
const LoginPage = lazy(() => import('./components/Auth/Login'));
const RegisterPage = lazy(() => import('./components/Auth/Register'));
const SignupSuccess = lazy(() => import('./components/Auth/SignupSuccess'));
const AuthCallback = lazy(() => import('./components/Auth/AuthCallback'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/Legal/CookiePolicy'));
const HelpCenter = lazy(() => import('./pages/Help/HelpCenter'));
const About = lazy(() => import('./pages/About/About'));
const Docs = lazy(() => import('./pages/Docs/Docs'));
const Guides = lazy(() => import('./pages/Guides/Guides'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

function RouteFallback() {
  const { t } = useTranslation();
  return <div className="loading-container"><div className="glass-loader">{t('auth.loadingSession')}</div></div>;
}

function App() {
  return (
    <GlobalErrorProvider>
      <AuthProvider>
        <Router>
          <RouteMetadata />
          <div className="App">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/agents" element={<AgentPage />} />
                <Route path="/products/automation" element={<Automation />} />
                <Route path="/products/apps" element={<Apps />} />
                {/* Legacy routes kept working so existing links/bookmarks don't break */}
                <Route path="/agents" element={<AgentPage />} />
                <Route path="/agent/:agentId" element={<AgentPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<RegisterPage />} />
                <Route path="/signup/success" element={<SignupSuccess />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/about" element={<About />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/guides" element={<Guides />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </GlobalErrorProvider>
  );
}

export default App;
